import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Loading } from '@arcflight/tf-component-library';
import { DashboardState } from '../../models';
import { Header } from '../../components/FlightDrawer/FlightDrawer';
import { IntermittentFault } from '../../models/intermittentFaults';
import DateTimePicker from '../../components/DateTimePicker/DateTimePicker';
import TFSearchableSelect from '../../components/TFSearchableSelect/TFSearchableSelect';
import { getFlightsForAircraft } from '../../models/flights/actions';
import createFlightOptions from '../../components/_utils/FlightOptionsBuilder';
import FlexWrapper from '../../components/DefectDrawer/components/FlexWrapper';
import useGetOperatorSetting from '../../utils/useGetOperatorSetting';

interface SectorSectionProps {
  fault: IntermittentFault;
  updateFaultData: (key: string, value: string, secondKey?: string, secondValue?: string) => void;
  aircraftId: string;
  localFlightId?: string;
  setLocalFlightId?: (id: string) => void;
}

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: ${({ isMobile }): string => {
    if (isMobile) return '1fr';
    return '1fr 1fr';
  }};
  grid-template-rows: auto;
  gap: 1rem;
`;

const SectorSection: React.FC<SectorSectionProps> = ({
  fault,
  updateFaultData,
  aircraftId,
  localFlightId,
  setLocalFlightId,
}) => {
  const {
    flights: { aircraftFlightsMaps },
    drawer: { mode },
    userSettings,
  } = useSelector((state: DashboardState) => state);
  const { formatMessage } = useIntl();
  const { id } = useParams<{ id: string }>();

  const [date, setDate] = useState('');
  const [flight, setFlight] = useState<{ title: string; value: string }>();
  const [flightOptions, setFlightOptions] = useState([]);

  const sectorNameOverride = useGetOperatorSetting('sector_name', id) || formatMessage({ id: 'text.sector' });

  const dispatch = useDispatch();

  useEffect(() => {
    const aircraft = aircraftFlightsMaps.get(aircraftId);
    if (!aircraft) {
      dispatch(getFlightsForAircraft({ payload: { id: aircraftId } }));
    } else {
      const parsedOptions = createFlightOptions(Array.from(aircraft.flightsMap.values()));
      setFlightOptions(parsedOptions);
    }
  }, [aircraftFlightsMaps, dispatch, aircraftId]);

  useEffect(() => {
    if (fault) {
      if (fault?.reported_at) setDate(fault?.reported_at);
      if (fault.flight_id) {
        const foundFlight = flightOptions.find((f) => f.value === fault.flight_id);
        setFlight(foundFlight);
        setLocalFlightId(fault.flight_id);
      }
    } else {
      const now = moment().utc().format();
      setDate(now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fault, flightOptions]);

  useEffect(() => {
    if (localFlightId) {
      const foundFlight = flightOptions.find((f) => f.value === localFlightId);
      setFlight(foundFlight);
    }
  }, [flightOptions, localFlightId]);

  const handleDateChange = (dateMoment: string): void => {
    setDate(dateMoment);
    updateFaultData('reported_at', dateMoment);
  };

  const handleFlightChange = (selectedFlight: { title: string; value: string }): void => {
    setFlight(selectedFlight);
    updateFaultData('flight_id', selectedFlight?.value);
  };

  const isMobile = window.innerWidth < 451;
  return (
    <>
      <ContentWrapper isMobile={isMobile} mode={mode}>
        <div>
          <Header editable={mode !== 'view'}>{sectorNameOverride}</Header>
          {mode === 'edit' || mode === 'add' ? (
            <>
              <Loading loading={false} contain />
              <TFSearchableSelect initial={flight} options={flightOptions} handleSelectChange={handleFlightChange} />
            </>
          ) : (
            <FlexWrapper>{flight?.title || '-'}</FlexWrapper>
          )}
        </div>
        <div>
          {mode === 'view' ? (
            <>
              <Header>Date Recorded</Header>
              <span>{moment(date).utc().format(`${userSettings.dateFormat} HH:mm`) || '-'}</span>
            </>
          ) : (
            <DateTimePicker
              headerDate="Date Recorded"
              headerTime="Time Recorded"
              handleDateTimeChange={handleDateChange}
              dateTime={date}
            />
          )}
        </div>
      </ContentWrapper>
    </>
  );
};

SectorSection.defaultProps = {
  localFlightId: '',
  setLocalFlightId: (): void => {},
};

export default SectorSection;
